import { useEffect, useState } from "react";
import InlineNote from "@/components/base/InlineNote";
import MicroLabel from "@/components/base/MicroLabel";

interface Stage {
  key: string;
  label: string;
  icon: string;
  ms: number;
  note: string;
}

type Mode = "retail" | "institutional";

const MODES: { key: Mode; label: string; short: string }[] = [
  { key: "retail", label: "Retail platform", short: "Retail" },
  { key: "institutional", label: "Institutional DMA", short: "Institutional" },
];

const TAGLINE: Record<Mode, string> = {
  retail:
    "Everyday setup — a broker-hosted terminal that routes the order onward to a liquidity provider.",
  institutional:
    "Direct market access — an API wired straight into a co-located matching engine.",
};

/* Retail: broker-hosted terminal routed onward. */
const RETAIL: Stage[] = [
  {
    key: "human",
    label: "You react & click",
    icon: "ri-user-voice-line",
    ms: 250,
    note: "From spotting the price to your finger landing on the button. No amount of broker speed can help you here.",
  },
  {
    key: "platform",
    label: "Terminal → broker",
    icon: "ri-computer-line",
    ms: 52,
    note: "Your platform packages the order and fires it at the broker's server.",
  },
  {
    key: "routing",
    label: "Broker decides",
    icon: "ri-git-branch-line",
    ms: 18,
    note: "The broker picks the destination — its own internal book or an external liquidity provider.",
  },
  {
    key: "fill",
    label: "Matching engine fills",
    icon: "ri-flashlight-line",
    ms: 5,
    note: "The venue matches the order and confirms the fill price. The fastest leg of the whole race.",
  },
];

/* Institutional: direct API into a co-located engine. */
const INSTITUTIONAL: Stage[] = [
  {
    key: "human",
    label: "You react & click",
    icon: "ri-user-voice-line",
    ms: 250,
    note: "Identical to the retail case — the human leg does not get faster just because the plumbing does.",
  },
  {
    key: "platform",
    label: "Order to API",
    icon: "ri-code-s-slash-line",
    ms: 12,
    note: "No retail terminal in the way — the order goes straight out through a direct API.",
  },
  {
    key: "routing",
    label: "Smart order router",
    icon: "ri-git-branch-line",
    ms: 6,
    note: "A dedicated low-latency link carries the order to the venue in single-digit milliseconds.",
  },
  {
    key: "fill",
    label: "Co-located engine fills",
    icon: "ri-flashlight-line",
    ms: 2,
    note: "The matching engine sits in the same data centre and confirms the fill in microseconds.",
  },
];

const sumMs = (stages: Stage[]) => stages.reduce((sum, stage) => sum + stage.ms, 0);

const RETAIL_TOTAL = sumMs(RETAIL);
const INST_TOTAL = sumMs(INSTITUTIONAL);
const RETAIL_MACHINE = RETAIL_TOTAL - RETAIL[0].ms;
const INST_MACHINE = INST_TOTAL - INSTITUTIONAL[0].ms;
const MACHINE_CUT = Math.round((1 - INST_MACHINE / RETAIL_MACHINE) * 100);
const TRIP_CUT = Math.round((1 - INST_TOTAL / RETAIL_TOTAL) * 100);

const INSIGHT: Record<Mode, string> = {
  retail:
    "Almost four fifths of this trip happens before your order even exists — spotting the price, deciding, moving your finger. Faster plumbing only shrinks the short end of it, which is why traders who plan the entry in advance get the fills that people who react never do.",
  institutional:
    "You can rent a faster pipe; you cannot rent a faster decision. At this level the machine legs are already close to the floor, so the only lever left is deciding before the market moves.",
};

export default function LatencyTimeline() {
  const [mode, setMode] = useState<Mode>("retail");
  const [activeKey, setActiveKey] = useState<string>("human");
  const [started, setStarted] = useState(false);

  const stages = mode === "retail" ? RETAIL : INSTITUTIONAL;
  const total = stages.reduce((sum, stage) => sum + stage.ms, 0);
  const humanShare = Math.round((stages[0].ms / total) * 100);

  /* Ticks are only kept if they leave room before the end label. */
  const ticks = [0, 100, 200].filter((tick) => tick < total * 0.94).concat(total);

  /* Animate the bars in, and re-run whenever the setup changes. */
  useEffect(() => {
    setStarted(false);
    const id = window.setTimeout(() => setStarted(true), 80);
    return () => window.clearTimeout(id);
  }, [mode]);

  let acc = 0;
  const rows = stages.map((stage) => {
    const start = acc;
    acc += stage.ms;
    return { ...stage, start };
  });

  return (
    <div className="border-2 border-foreground-950 bg-background-50">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 bg-foreground-950 border-b-2 border-foreground-950">
        <MicroLabel tone="onDarkPrimary">Click-to-fill · where the time goes</MicroLabel>
        <div className="flex items-center border-2 border-background-50/25">
          {MODES.map((item) => {
            const active = item.key === mode;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setMode(item.key)}
                aria-pressed={active}
                className={`px-3 py-1.5 font-label text-[10px] uppercase tracking-[0.18em] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  active
                    ? "bg-primary-500 text-foreground-950"
                    : "text-background-300 hover:text-primary-500"
                }`}
              >
                <span className="hidden sm:inline">{item.label}</span>
                <span className="sm:hidden">{item.short}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-5 md:p-6">
        {/* Summary — three columns, same anatomy: label / figure / caption */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-6 border-b-2 border-foreground-950 pb-5">
          <div className="flex flex-col sm:pr-6">
            <MicroLabel tone="muted">Order out</MicroLabel>
            <p className="mt-2 font-heading font-black leading-none text-foreground-950 text-4xl md:text-5xl whitespace-nowrap">
              {total}
              <span className="ml-1 text-2xl md:text-3xl">ms</span>
            </p>
            <p className="mt-auto pt-3 text-xs leading-relaxed text-foreground-600">
              ≈ one blink of an eye
            </p>
          </div>

          <div className="flex flex-col sm:px-6 sm:border-l-2 sm:border-foreground-950">
            <MicroLabel tone="muted">Retail → institutional</MicroLabel>
            <p className="mt-2 font-heading font-black leading-none text-foreground-950 text-3xl md:text-4xl tabular-nums whitespace-nowrap">
              −{TRIP_CUT}%
            </p>
            <p className="mt-auto pt-3 text-xs leading-relaxed text-foreground-600">
              whole trip faster · machine legs −{MACHINE_CUT}%
            </p>
          </div>

          <div className="flex flex-col sm:pl-6 sm:border-l-2 sm:border-foreground-950">
            <MicroLabel tone="muted">Your share of the trip</MicroLabel>
            <p className="mt-2 font-heading font-black leading-none text-accent-600 text-3xl md:text-4xl tabular-nums whitespace-nowrap">
              {humanShare}%
            </p>
            <p className="mt-auto pt-3 text-xs leading-relaxed text-foreground-600">
              the slowest leg of the race
            </p>
          </div>
        </div>

        <p className="mt-4 text-xs md:text-sm leading-relaxed text-foreground-600">
          {TAGLINE[mode]}
        </p>

        {/* Gantt rows */}
        <div className="mt-5 flex flex-col gap-5">
          {rows.map((row, index) => {
            const active = row.key === activeKey;
            const isHuman = row.key === "human";
            return (
              <button
                key={row.key}
                type="button"
                onClick={() => setActiveKey((prev) => (prev === row.key ? "" : row.key))}
                aria-expanded={active}
                className="group w-full text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-4 focus-visible:ring-offset-background-50"
              >
                <span className="flex items-center gap-4">
                  <span
                    className={`w-10 h-10 shrink-0 flex items-center justify-center border-2 border-foreground-950 transition-colors ${
                      isHuman
                        ? "bg-accent-500 text-foreground-950"
                        : "bg-background-50 text-foreground-950 group-hover:bg-background-200"
                    }`}
                  >
                    <i className={`${row.icon} text-xl`}></i>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-3">
                      <span className="font-heading font-bold uppercase tracking-tight text-sm md:text-base text-foreground-950 transition-colors group-hover:text-accent-700">
                        {row.label}
                      </span>
                      <span className="font-label text-xs md:text-sm font-semibold tabular-nums text-foreground-950 whitespace-nowrap transition-colors group-hover:text-accent-700">
                        {row.ms} ms
                      </span>
                    </span>
                  </span>
                </span>

                {/* Proportional track */}
                <span className="mt-3 pl-14 block">
                  <span className="relative h-3.5 border-2 border-foreground-950 bg-background-100 overflow-hidden block">
                    <span
                      className={`absolute inset-y-0 transition-all duration-700 ease-out ${
                        isHuman ? "bg-accent-500" : "bg-foreground-950"
                      }`}
                      style={{
                        left: `${(row.start / total) * 100}%`,
                        width: started ? `${(row.ms / total) * 100}%` : "0%",
                        minWidth: started ? "5px" : "0",
                        transitionDelay: `${index * 180}ms`,
                      }}
                    ></span>
                  </span>
                </span>

                {active && (
                  <span className="mt-2 pl-14 block text-xs md:text-sm leading-relaxed text-foreground-600">
                    {row.note}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Time ruler — ticks sit at their real position on the track */}
        <div className="mt-2 pl-14" aria-hidden="true">
          <div className="relative h-4 mx-[2px]">
            {ticks.map((tick) => {
              const pct = (tick / total) * 100;
              const transform =
                pct === 0 ? "translateX(0)" : pct === 100 ? "translateX(-100%)" : "translateX(-50%)";
              return (
                <span
                  key={tick}
                  className="absolute top-0 font-label text-[10px] uppercase tracking-[0.18em] text-foreground-600 tabular-nums whitespace-nowrap"
                  style={{ left: `${pct}%`, transform }}
                >
                  {tick === total ? `${tick} ms` : tick}
                </span>
              );
            })}
          </div>
        </div>

        {/* Legend + note on what these figures are */}
        <div className="mt-4 border-t-2 border-foreground-950 pt-4">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <MicroLabel mark="dot" tone="accent">
              Human leg
            </MicroLabel>
            <MicroLabel mark="dot" tone="default">
              Machine legs
            </MicroLabel>
          </div>
          <p className="mt-3 text-xs md:text-sm leading-relaxed text-foreground-700">
            Orange is you; black is the machines. Click a row to read what happens in it.
          </p>
          <p className="mt-3 text-xs leading-relaxed text-foreground-500">
            One-way only — the confirmation back is not included. Indicative timings for a typical
            account; real figures vary with broker, venue and your connection.
          </p>
        </div>
      </div>

      <InlineNote
        mark="arrow"
        label="Why it matters"
        className="bg-primary-100 px-5 md:px-6 py-4"
      >
        {INSIGHT[mode]}
      </InlineNote>
    </div>
  );
}